import { Component, ElementRef, Renderer2, OnInit, OnDestroy } from "@angular/core";
import { Router, NavigationEnd } from "@angular/router";
import { KeycloakService } from "keycloak-angular";
import { KeycloakProfile } from "keycloak-js";
import { AuthService } from "../../services/auth/auth.service";
import { MenuTree } from "../../shared/utils/menu-tree";
import { Role } from "../../shared/utils/role.model";
import { TranslateService } from "@ngx-translate/core";
import { filter, Subscription } from "rxjs";
import { CompanyApiService } from "../../services/generated/waba-api/services/company-api.service";
import { v4 as uuidv4 } from "uuid";

@Component({
  selector: "app-sidebar",
  templateUrl: "./sidebar.component.html",
  styleUrl: "./sidebar.component.scss",
})
export class SidebarComponent implements OnInit, OnDestroy {
  protected readonly allMenus = MenuTree;
  showSubItems: boolean = false;
  currentPathSubItems: string | undefined;
  profile!: KeycloakProfile | null;
  userGroups: string[] = [];
  companyActivity: string | undefined;
  private routerSubscription?: Subscription;

  constructor(
    private authService: AuthService,
    private keycloakService: KeycloakService,
    private translate: TranslateService,
    private renderer: Renderer2,
    private el: ElementRef,
    private router: Router,
    private companyAdminService: CompanyApiService
  ) {
    this.translate.onLangChange.subscribe((event) => {
      this.checkLanguageDirection(event.lang);
    });
  }

  async ngOnInit() {
    await this.loadUserInfo();
    this.checkLanguageDirection();
    
    // Subscribe to router navigation events to close sidebar on navigation
    this.routerSubscription = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        this.closeDrawer();
      });
  }

  ngOnDestroy() {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }

  async loadUserInfo() {
    // Load user profile
    this.profile = await this.authService.getProfile();
    // Load user groups from Keycloak
    await this.loadUserGroups();
    // Load company activity to conditionally show/hide reservation menu
    this.loadCompanyActivity();
  }

  loadCompanyActivity() {
    const companyId = this.authService.getCompanyId();
    if (companyId) {
      this.companyAdminService
        .getActivity({
          "x-api-requestId": uuidv4(),
          "x-api-canal": "web",
          companyId: companyId,
        })
        .subscribe({
          next: (response) => {
            this.companyActivity = response.body;
          },
          error: () => {
            this.companyActivity = undefined;
          },
        });
    }
  }

  async loadUserGroups() {
    try {
      this.userGroups = await this.authService.getUserGroups();
    } catch (error) {
      console.warn("Could not load user groups:", error);
      this.userGroups = [];
    }
  }

  signout() {
    this.authService.logout();
  }

  goToProfile() {
    this.authService.accountManagement();
  }

  toggleSubItems(path: string | undefined) {
    if (path === this.currentPathSubItems) {
      this.showSubItems = !this.showSubItems;
    } else {
      this.showSubItems = true;
    }
    this.currentPathSubItems = path;
  }

  /**
   * Closes the drawer programmatically
   * This method mimics the behavior of clicking the drawer hide button
   */
  closeDrawer(): void {
    const drawerElement = document.getElementById('drawer-navigation');
    if (drawerElement) {
      // Find the hide button and trigger click, or manually hide the drawer
      const hideButton = document.querySelector('[data-drawer-hide="drawer-navigation"]') as HTMLElement;
      if (hideButton) {
        hideButton.click();
      } else {
        // Fallback: manually hide the drawer by removing the 'translate-x-0' class
        // Flowbite drawer is shown when it has 'translate-x-0' class
        drawerElement.classList.remove('translate-x-0');
        if (!this.isRTL) {
          drawerElement.classList.add('-translate-x-full');
        } else {
          drawerElement.classList.add('translate-x-full');
        }
        // Remove backdrop if exists
        const backdrop = document.querySelector('[data-drawer-backdrop]');
        if (backdrop) {
          backdrop.remove();
        }
      }
    }
  }

  /**
   * Handles submenu item click and closes the drawer
   */
  onSubmenuClick(): void {
    // Close drawer when submenu item is clicked
    // The router navigation will also trigger closeDrawer via NavigationEnd event
    // but we call it here immediately for better UX
    setTimeout(() => {
      this.closeDrawer();
    }, 100);
  }

  isUserAuthorized(menuItem: any): boolean {
    // Activity-based visibility: hide if current activity matches hideForActivity
    if (menuItem.hideForActivity && this.companyActivity === menuItem.hideForActivity) {
      return false;
    }
    // Activity-based visibility: show only if current activity matches activityCondition
    if (menuItem.activityCondition) {
      return this.companyActivity === menuItem.activityCondition;
    }
    // If no groups are configured, allow access
    if (!menuItem.groups || menuItem.groups.length === 0) {
      return true;
    }
    // Check group access
    const hasGroupAccess = this.userGroups.some((group) =>
      menuItem.groups.includes(group)
    );
    if (hasGroupAccess) return true;
    // Fallback to role-based check (backward compatibility)
    return false;
  }

  isUserInRole(roles: string[] | undefined) {
    if (!roles || roles.length === 0) {
      return true;
    }
    if (roles.includes(Role.ADMIN_SOLUTION_UI)) {
      return roles.every((role) => this.keycloakService.isUserInRole(role));
    } else {
      return roles.some((role) => this.keycloakService.isUserInRole(role));
    }
  }

  // -------------------------
  isRTL: boolean = false;
  checkLanguageDirection(lang?: string) {
    const currentLang = lang || sessionStorage.getItem("lang") || "fr";
    this.isRTL = currentLang === "ar";
    if (this.isRTL) {
      this.renderer.setAttribute(this.el.nativeElement, "dir", "rtl");
      this.renderer.addClass(this.el.nativeElement, "rtl");
    } else {
      this.renderer.setAttribute(this.el.nativeElement, "dir", "ltr");
      this.renderer.removeClass(this.el.nativeElement, "rtl");
    }
  }
}
